import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["focusedValue", "maxValidationMessage", "minValidationMessage", "children", "disabledValues", "leftControl", "max", "min", "month", "onDateClick", "onDateMouseEnter", "onDateMouseLeave", "ownerId", "rightControl", "today", "weekdayHeadings", "weekStartDay", "year", "onMouseDown"];
import { SimpleDate, compare, equals } from '../../utils/SimpleDate';
import I18n from 'I18n';
import styled from 'foundations-theming/utils';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { lazyEval } from '../../utils/lazyEval';
import { useMemo } from 'react';
// @ts-ignore formatMediumMonthYear is not exported
import formatMediumMonthYear from 'I18n/utils/formatMediumMonthYear';
import { Text } from '../../../components/Text';
import { StyledText } from '../../../components/Text/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Moves values from the start of the array to the end.
 * @param arr
 * @param offset
 * @returns string
 */
const rotateArrayBy = (arr, offset) => {
  const arrCopy = arr.slice();
  for (let i = 0; i < offset; i++) {
    const firstValue = arrCopy.shift();
    if (firstValue !== undefined) {
      arrCopy.push(firstValue);
    }
  }
  return arrCopy;
};

/**
 * Wrapper for the `SimpleDate` constructor that provides fallover if `month` is outside of the
 * valid range (0 - 11), using the native `Date` API.
 */
const safeSimpleDate = (year, month, date) => {
  const dateObject = new Date(year, month, date);
  return new SimpleDate(dateObject.getFullYear(), dateObject.getMonth(), dateObject.getDate());
};

/**
 * @param year What year is it!?
 * @param month 0 for Jan, 1 for Feb, etc., à la Date
 * @param weekStartDay 0 for Sunday, 1 for Monday
 * @returns An array of arrays, where each array is a row of the calendar
 */
const computeWeeksOfMonth = (year, month, weekStartDay) => {
  // Per the Date API convention, "date" = day of month, "day" = day of week.
  const monthStartDay = new Date(year, month).getDay();
  const monthEndDate = new Date(year, month + 1, 0).getDate();
  const weekEndDay = (weekStartDay + 6) % 7;
  const weeks = [[]];

  // Get trailing dates from the previous month
  const startOffset = (monthStartDay - weekStartDay + 7) % 7 - 1;
  const prevMonthEndDate = new Date(year, month, 0).getDate();
  for (let date = prevMonthEndDate - startOffset; date <= prevMonthEndDate; date++) {
    weeks[0].push(safeSimpleDate(year, month - 1, date));
  }
  let week = 0;
  let day = monthStartDay;
  for (let date = 1; date <= monthEndDate; date++) {
    weeks[week].push(safeSimpleDate(year, month, date));
    if (day === weekEndDay) {
      week++;
      weeks.push([]);
    }
    day = (day + 1) % 7;
  }

  // Add leading dates for the next month until we have a full 6 weeks
  const endOffset = 7 * (6 - weeks.length) + 7 - weeks[week].length;
  for (let date = 1; date <= endOffset; date++) {
    weeks[week].push(safeSimpleDate(year, month + 1, date));
    if (day === weekEndDay && date !== monthEndDate) {
      week++;
      weeks.push([]);
    }
    day = (day + 1) % 7;
  }
  return weeks;
};

/**
 * Returns the date corresponding to a Day from the DOM, based on its `data-` attrs.
 *
 * @param element
 * @returns SimpleDate
 */
export const getDateForElement = element => new SimpleDate(+element.getAttribute('data-year'), +element.getAttribute('data-month'), +element.getAttribute('data-date'));

/**
 * Returns the locale-specific, formatted month-year string.
 *
 * @param year
 * @param month
 * @returns string
 */
export const createFormattedMonthYear = (month, year) => {
  const dateValue = I18n.moment({
    year,
    month
  });
  return formatMediumMonthYear(dateValue);
};
export const Wrapper = styled.div.withConfig({
  displayName: "AbstractMonth__Wrapper"
})(() => {
  return {
    cursor: 'default',
    inlineSize: '260px',
    blockSize: '260px'
  };
});
export const Header = styled.div.withConfig({
  displayName: "AbstractMonth__Header"
})(() => {
  return {
    display: 'flex',
    blockSize: '40px',
    alignItems: 'center',
    [`${StyledText}`]: {
      marginInline: 'auto',
      marginBlock: 0,
      display: 'inline-block'
    }
  };
});
export const Table = styled.table.withConfig({
  displayName: "AbstractMonth__Table"
})([".hs-private.hs-private.hs-private &{width:100%;table-layout:fixed;border-collapse:collapse;text-align:center;font-size:15px;}"]);
export const WeekdayHeading = styled.th.withConfig({
  displayName: "AbstractMonth__WeekdayHeading"
})(({
  theme
}) => {
  return {
    paddingBlock: theme.space[200],
    paddingInline: theme.space[0],
    color: theme.color['text-core-subtle'],
    fontWeight: theme.typography['heading-400'].fontWeight
  };
});
export const AbstractMonth = _ref => {
  let {
      focusedValue,
      maxValidationMessage,
      minValidationMessage,
      children,
      disabledValues,
      leftControl,
      max,
      min,
      month,
      onDateClick,
      onDateMouseEnter,
      onDateMouseLeave,
      ownerId,
      rightControl,
      today,
      weekdayHeadings,
      weekStartDay,
      year,
      onMouseDown
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Convert year and month values to I18N timestamp to make use of format utils #8640
  const formattedMonthYear = useMemo(() => createFormattedMonthYear(month, year), [month, year]);
  const abbreviateWeekday = weekday => weekday.slice(0, 2);
  const abbreviatedWeekdayHeadings = weekdayHeadings.map(abbreviateWeekday);
  return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
    children: [/*#__PURE__*/_jsxs(Header, {
      children: [leftControl, /*#__PURE__*/_jsx(Text, {
        as: "h5",
        "aria-hidden": true,
        typography: "heading-100",
        children: formattedMonthYear
      }), rightControl]
    }), /*#__PURE__*/_jsxs(Table, {
      role: "presentation",
      children: [/*#__PURE__*/_jsx("thead", {
        "aria-hidden": true,
        children: /*#__PURE__*/_jsx("tr", {
          children: rotateArrayBy(abbreviatedWeekdayHeadings, weekStartDay).map((weekdayHeading, i) => /*#__PURE__*/_jsx(WeekdayHeading, {
            children: weekdayHeading
          }, i))
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: computeWeeksOfMonth(year, month, weekStartDay).map((week, i) => /*#__PURE__*/_jsx("tr", {
          children: week.map((simpleDate, index) => {
            const lessThanMin = min && compare(simpleDate, min) === -1;
            const moreThanMax = max && compare(simpleDate, max) === 1;
            const inDisabledValues = disabledValues && disabledValues.some(disabledValue => equals(simpleDate, disabledValue));
            const isDisabled = lessThanMin || moreThanMax || inDisabledValues;
            const isFocused = equals(simpleDate, focusedValue);
            return children(simpleDate, {
              id: `${ownerId}-${simpleDate.month}-${simpleDate.date}`,
              'data-date': simpleDate.date,
              'data-month': simpleDate.month,
              'data-year': simpleDate.year,
              key: index,
              role: 'menuitem',
              onClick: isDisabled ? undefined : evt => {
                if (!onDateClick) return;
                onDateClick(syntheticEvent(getDateForElement(evt.currentTarget)));
              },
              onMouseEnter: isDisabled || !onDateMouseEnter ? undefined : evt => {
                onDateMouseEnter(syntheticEvent(getDateForElement(evt.currentTarget)));
              },
              onMouseDown,
              onMouseLeave: isDisabled || !onDateMouseLeave ? undefined : evt => {
                onDateMouseLeave(syntheticEvent(getDateForElement(evt.currentTarget)));
              },
              isCurrentMonth: simpleDate.month === month,
              isDisabled,
              isToday: equals(simpleDate, today),
              tooltip: lazyEval((lessThanMin ? minValidationMessage : '') || (moreThanMax ? maxValidationMessage : '')),
              tooltipOpen: isFocused || undefined
            });
          })
        }, i))
      })]
    })]
  }));
};